/**
 * Standalone action handlers extracted from the MedicalPaOrders page component.
 * Covers date-range export, processed-orders stats fetching, and bulk reassignment.
 * @module medicalPaOrdersHandlers
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { limit, orderBy, where } from "firebase/firestore";
import moment from "moment";
import React from "react";
import { FirestoreService } from "../../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../api/firebase/references";
import { BulkOrderUpdateForAssignmentResponse } from "../../../../api/postgreSQL/postCalls/bulkOrderUpdateForAssignment";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { EXPORT_DATA_LIMIT } from "../../../../constants/medicalPAbatch";
import {
  UNASSIGNED_TEXT,
  UNASSIGNED_TEXT_LABEL,
} from "../../../../constants/stringLiterals";
import { MedicalPaOrder } from "../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { closeModal as closeReduxModal } from "../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../redux/store/store";
import { logError } from "../../../../utils/customLogger";
import {
  exportDataFromTableForMedicalPaOrders,
  exportDataToExcel,
} from "../../../../utils/exportDataToExcel";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import {
  insertOrderAssignedReassignedTrailComment,
  OrderTrailOrderContext,
} from "../../../../utils/orderTrailComment";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import {
  fetchProcessedOrdersData,
  ProcessedOrdersData,
} from "../../medicalPaOrdersAlgolia/utils/fetchProcessedOrdersData";

export type { ProcessedOrdersData };

/** Default/empty state for {@link ProcessedOrdersData}. */
export const EMPTY_PROCESSED_ORDERS: ProcessedOrdersData = {
  completion_percentage: 0,
  orders_processed: 0,
  orders_remaining: 0,
  time_taken: "",
  loading: false,
  skipped_duplicate_order_ids: [],
};

/** Provider details required by the batch reassignment handler. */
interface ProviderInfo {
  id: string;
  FirstName?: string;
  LastName?: string;
  name?: string;
}

/** Bundled callbacks for {@link handleReassignInBatchMode}. */
interface ReassignCallbacks {
  runUpdate: (
    orderIds: string[],
    data: Record<string, string>,
  ) => Promise<{
    success: boolean;
    data: BulkOrderUpdateForAssignmentResponse | null;
    error?: unknown;
  }>;
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  dispatch: AppDispatch;
  user: { id?: string; email?: string | null } | null;
  orgId: string;
  providerName: string;
  providerEmail: string;
}

/**
 * Fetches medical PA orders within a date range from Firestore and exports them to Excel.
 *
 * @param startDate - Start of the export date range
 * @param endDate - End of the export date range
 * @param setShowLoading - Loading state setter for the export operation
 */
export const handleDataFetchingForSelectedDateRange = async (
  startDate: Date,
  endDate: Date,
  setShowLoading: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  try {
    setShowLoading(true);
    const startDateForExport = moment(startDate)
      .startOf("day")
      .format("YYYY-MM-DD HH:mm:ss");
    const endOfDayendDateForExport = moment(endDate)
      .endOf("day")
      .format("YYYY-MM-DD HH:mm:ss");
    const data = await FirestoreService.getDocumentsByQuery(
      FirestoreCollectionReference.medicalPaOrders(),
      [
        where("created_at", ">=", startDateForExport),
        where("created_at", "<=", endOfDayendDateForExport),
        where(
          "assigned_to.facility_id",
          "==",
          getOrgIdForFetchExternalWorklist(),
        ),
        orderBy("created_at", "desc"),
        limit(EXPORT_DATA_LIMIT),
      ],
    );
    const exportedData = exportDataFromTableForMedicalPaOrders(
      data as MedicalPaOrder[],
    );
    exportDataToExcel(
      exportedData,
      `medicalPaOrders-${moment().format("YYYY-MM-DD")}`,
    );
  } catch (error) {
    logError(error as Error, "Error exporting data for date range");
  } finally {
    setShowLoading(false);
  }
};

/**
 * Fetches today's order-processing statistics and updates state.
 *
 * @param setProcessedOrdersToday - State setter for the processed-orders statistics
 */
export const fetchProcessedOrdersToday = async (
  setProcessedOrdersToday: React.Dispatch<
    React.SetStateAction<ProcessedOrdersData>
  >,
) => {
  try {
    setProcessedOrdersToday((prev) => ({ ...prev, loading: true }));
    const data = await fetchProcessedOrdersData();
    setProcessedOrdersToday(data);
  } catch {
    setProcessedOrdersToday(EMPTY_PROCESSED_ORDERS);
  }
};

/**
 * Batch-reassigns selected orders to a provider via the Postgres bulk-update API.
 * Logs the bulk assignment event and shows success/error toasts.
 *
 * @param selectedProvider - Provider ID to assign, or "unassigned" to clear assignment
 * @param selectedOrders - Selected orders with IDs and patient context for trail comments
 * @param providersDetails - Full list of available providers
 * @param callbacks - State setters, dispatch, user info, and the bulk-update function
 */
export const handleReassignInBatchMode = async (
  selectedProvider: string,
  selectedOrders: OrderTrailOrderContext[],
  providersDetails: ProviderInfo[] | undefined,
  callbacks: ReassignCallbacks,
) => {
  const {
    runUpdate,
    setIsUpdating,
    setIsSuccess,
    dispatch,
    user,
    orgId,
    providerName,
    providerEmail,
  } = callbacks;
  const selectedOrderIds = selectedOrders.map((order) => order.orderId);
  const selectedProviderDetails = providersDetails?.find(
    (provider) => provider.id === selectedProvider,
  ) ?? { FirstName: UNASSIGNED_TEXT, LastName: "", id: UNASSIGNED_TEXT };
  setIsUpdating(true);
  setIsSuccess(false);
  if (selectedProviderDetails) {
    const fullName = generateFullName(
      selectedProviderDetails.FirstName || selectedProviderDetails.name || "",
      "",
      selectedProviderDetails.LastName || "",
    );
    const providerId = selectedProviderDetails.id;
    const updates = {
      assigned_to_name: fullName,
      assigned_to_id: providerId,
    };
    apiWorkerManager.enqueueAndProcess({
      event_name: EventsName.BULK_ASSIGN_BUTTON_SAVE_CLICKED,
      patient_id: "",
      order_id: "",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      org_id: orgId ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.WORKLIST,
        user_id: user?.id ?? "",
        number_of_orders: selectedOrderIds.length,
        assigned_to: fullName,
      },
    });
    try {
      const result = await runUpdate(selectedOrderIds, updates);
      if (result.success && result.data?.success) {
        const toAssignee =
          providerId === UNASSIGNED_TEXT ? UNASSIGNED_TEXT_LABEL : fullName;

        selectedOrders.forEach(({ orderId, patientId, fromAssignee }) => {
          insertOrderAssignedReassignedTrailComment({
            orderId,
            orgId,
            patientId,
            providerEmail,
            providerName,
            fromAssignee: fromAssignee ?? UNASSIGNED_TEXT_LABEL,
            toAssignee,
          });
        });

        setIsSuccess(true);
      } else {
        logError(result.error as Error, "Error in batch assignment");
        toast.error(ToastMessages.ASSIGN_ORDER_FAILED);
      }
    } catch (error) {
      logError(error as Error, "Error in batch assignment");
      toast.error(ToastMessages.ASSIGN_ORDER_FAILED);
    } finally {
      setIsUpdating(false);
      dispatch(closeReduxModal());
    }
  }
};
